/**
 * ErrorState primitive (Plan 06-01, Task 1).
 *
 * Standardised error fallback used both inline (inside a <Card>) and at
 * page-level (full-route fallback). Replaces ad-hoc `isError && null` /
 * `isError && <p>` patterns across every Phase 1-5 feature surface (see
 * 06-UI-SPEC.md §"Retroactive application rule").
 *
 * Variants:
 *   inline (default) — Card-shaped block; Icon.AlertShield 32px in
 *                       text-bad; heading 17/600;
 *                       optional retry CTA.
 *   page              — Full-viewport fallback; Icon 48px; heading
 *                       Display S 28/600.
 *
 * ARIA: `role="alert"` on the root so screen-readers announce the
 *       heading immediately when the component mounts.
 * Reduced motion: no animation; static layout.
 * Norwegian copy: retry button label comes from `errors.retry` (defined
 *                 in 06-01 Task 4) — never hard-coded.
 *
 * Author note: React 19 — no forwardRef needed (refs are regular props).
 *
 * Not for a submit that failed inside a BottomSheet — that is SheetError
 * (issue #78). This component always renders an element and owns a fixed
 * `data-testid`, so it fits neither a sheet's idle state nor a call site
 * with its own testid.
 */
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from './Card';
import { Button } from './Button';
import { Icon } from './Icon';

export interface ErrorStateProps {
  /** `inline` — fits inside a <Card>. `page` — full-route fallback. */
  variant?: 'inline' | 'page';
  heading: string;
  body?: string;
  onRetry?: () => void;
}

export function ErrorState({
  variant = 'inline',
  heading,
  body,
  onRetry,
}: ErrorStateProps): JSX.Element {
  const { t } = useTranslation();
  const retryLabel = t('errors.retry');

  if (variant === 'page') {
    return (
      <div
        role="alert"
        data-testid="error-state"
        className="flex flex-col items-center justify-center gap-4 min-h-vh-60 px-6 text-center"
      >
        <Icon.AlertShield size={48} className="text-bad" />
        <h1 className="text-display-s font-extrabold tracking-tight text-ink">{heading}</h1>
        {body ? <p className="text-sm text-ink-2 max-w-80">{body}</p> : null}
        {onRetry ? (
          <Button
            variant="primary"
            size={52}
            aria-label={retryLabel}
            onClick={onRetry}
          >
            {retryLabel}
          </Button>
        ) : null}
      </div>
    );
  }

  // inline
  return (
    <Card variant="soft" className="!p-0">
      <div
        role="alert"
        data-testid="error-state"
        className="flex flex-col items-center justify-center gap-3 py-8 px-4"
      >
        <Icon.AlertShield size={32} className="text-bad" />
        <h2 className="text-heading-s font-extrabold text-ink">{heading}</h2>
        {body ? (
          <p className="text-sm text-ink-2 max-w-70 text-center">{body}</p>
        ) : null}
        {onRetry ? (
          <Button
            variant="primary"
            aria-label={retryLabel}
            onClick={onRetry}
          >
            {retryLabel}
          </Button>
        ) : null}
      </div>
    </Card>
  );
}
